import type { ItemCategory } from "@/lib/types";
import { categoryLabels } from "@/lib/categoryLabels";
import { MARKET_COPY } from "@/lib/market/copy";

export interface MarketResultsSummaryInput {
  total: number;
  searchQuery: string;
  /** Selected category id, or "all" when no category filter is active. */
  selectedCategory: ItemCategory | "all";
  /** Manual location filter, or "" when none is applied. */
  selectedLocation: string;
  /**
   * Buyer's profile/browser area (Phase 3). Used for benefit-first "near {area}"
   * grammar when no manual location filter is active. Optional so callers that
   * lack location context still get correct generic copy.
   */
  nearYouArea?: string;
}

/**
 * Single owner of market results-truth grammar.
 *
 * Phase 3: adds benefit-first location copy ("{n} items near {area}") using the
 * manual location filter first, then the buyer's profile/browser area. Catalog
 * cap honesty is built separately via `buildMarketCatalogCapNotice`.
 */
export function buildMarketResultsSummary({
  total,
  searchQuery,
  selectedCategory,
  selectedLocation,
  nearYouArea = "",
}: MarketResultsSummaryInput): string {
  const countLabel = total === 1 ? "item" : "items";
  const categorySuffix =
    selectedCategory !== "all" ? ` in ${categoryLabels[selectedCategory]}` : "";

  // Prefer the explicit location filter; fall back to the buyer's known area.
  const area = selectedLocation.trim() || nearYouArea.trim();
  const areaSuffix = area ? ` near ${area}` : "";

  if (searchQuery) {
    if (total === 0) {
      return `${MARKET_COPY.resultsNoItemsFound} for "${searchQuery}"${categorySuffix}${areaSuffix}`;
    }
    const matchLabel = total === 1 ? "match" : "matches";
    return `${total} ${matchLabel} for "${searchQuery}"${categorySuffix}${areaSuffix}`;
  }

  return `${total} ${countLabel}${categorySuffix}${areaSuffix}`;
}

/**
 * Builds the honest catalog-cap notice (D3). Callers gate this on the browse
 * being unfiltered AND at the fetch cap (see `catalogCapped` in useMarketBrowse);
 * this function only formats the count into the locked helper string.
 */
export function buildMarketCatalogCapNotice(total: number): string {
  return MARKET_COPY.capHelper.replace("{n}", String(total));
}
